import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Loader2, ArrowLeft, Building2, ExternalLink } from "lucide-react";
import { publicApi, publicError } from "@/lib/publicApi";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { SectionTitle, CheckPill } from "@/components/public/Primitives";
import CTASection from "@/components/public/CTASection";
import SEO from "@/components/public/SEO";
import NotFound from "@/pages/NotFound";

export default function CaseStudyDetail() {
    const { slug } = useParams();
    const [item, setItem] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        setItem(null);
        setError(null);
        publicApi
            .get(`/case-studies/${slug}`)
            .then((r) => setItem(r.data))
            .catch((e) => setError(e?.response?.status === 404 ? "notfound" : publicError(e)));
    }, [slug]);

    if (error === "notfound") return <NotFound message="That case study doesn't exist." backTo="/case-studies" backLabel="Back to Case Studies" />;
    if (!item) return <div className="min-h-[60vh] grid place-items-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div>;

    const origin =
        process.env.REACT_APP_SITE_URL ||
        (typeof window !== "undefined" ? window.location.origin : "");
    const canonical = `${origin}/case-studies/${item.slug}`;
    const caseJsonLd = {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: item.title,
        description: item.problem ? String(item.problem).slice(0, 200) : "",
        author: { "@type": "Organization", name: "Omega IT Ltd" },
        publisher: {
            "@type": "Organization",
            name: "Omega IT Ltd",
            logo: { "@type": "ImageObject", url: `${origin}/og-default.png` },
        },
        mainEntityOfPage: canonical,
    };

    return (
        <div data-testid={`case-study-detail-${item.slug}`}>
            <SEO
                title={item.seo_title || item.title}
                description={item.seo_description || (item.problem ? String(item.problem).slice(0, 155) : "")}
                canonical={canonical}
                ogType="article"
                ogImage={item.og_image || (item.images && item.images[0])}
                jsonLd={caseJsonLd}
            />
            <section className="bg-white border-b border-slate-100">
                <div className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12 py-16 lg:py-20">
                    <Link to="/case-studies" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-slate-950"><ArrowLeft className="w-4 h-4" /> All case studies</Link>
                    <div className="mt-6 flex flex-wrap items-center gap-3">
                        <Badge variant="outline" className="border-slate-200 text-slate-700">{item.client_business_type}</Badge>
                        {item.is_featured && <Badge className="bg-slate-950 text-white">Featured</Badge>}
                    </div>
                    <h1 className="font-display font-black tracking-tight text-slate-950 text-3xl sm:text-4xl lg:text-5xl mt-4 leading-[1.06]">{item.title}</h1>
                    {item.website_url && (
                        <a
                            href={item.website_url}
                            target="_blank"
                            rel="noopener nofollow"
                            data-testid="case-study-website-link"
                            className="mt-4 inline-flex items-center gap-1.5 font-semibold hover:underline"
                            style={{ color: "var(--violet-bright, #7C6BFF)", fontSize: "15px" }}
                        >
                            Visit website
                            <ExternalLink className="w-4 h-4" />
                        </a>
                    )}
                </div>
            </section>

            {item.images?.length > 0 && (
                <section className="px-5 sm:px-8 lg:px-12 py-10">
                    <div className="max-w-5xl mx-auto grid sm:grid-cols-2 gap-4">
                        {item.images.map((src, i) => (<img key={i} src={src} alt="" className="w-full rounded-2xl border border-slate-200" />))}
                    </div>
                </section>
            )}

            <section className="px-5 sm:px-8 lg:px-12 py-12">
                <div className="max-w-4xl mx-auto space-y-12">
                    <Block title="The problem" body={item.problem} />
                    <Block title="What we built" body={item.solution} />
                    {item.features_delivered?.length > 0 && (
                        <div>
                            <div className="label-eyebrow mb-3">Features delivered</div>
                            <div className="flex flex-wrap gap-2">
                                {item.features_delivered.map((f, i) => (<Badge key={i} variant="outline" className="border-slate-200 text-slate-700 py-1.5 px-3">{f}</Badge>))}
                            </div>
                        </div>
                    )}
                    <Block title="The results" body={item.results} />
                </div>
            </section>

            <CTASection title="Want a similar result for your business?" description="We work with restaurants, salons, retailers and tradespeople across the UK. Let's see what we can build for you." />
        </div>
    );
}

function Block({ title, body }) {
    return (
        <div>
            <div className="label-eyebrow mb-3">{title}</div>
            <p className="text-slate-700 leading-relaxed whitespace-pre-line">{body}</p>
        </div>
    );
}
